import { useState } from 'react';
import { Outlet, useNavigate, useOutletContext, useParams } from 'react-router-dom';
import { TabLink } from '../../components/ui/scroll-reset';
import type { EventRecord } from '@ni/shared';
import { useDeleteEvent, useEvent, useUntrackEvent } from '../../api/event-hooks';
import { loadErrorLine } from '../../api/errors';
import { RenameRecordModal } from '../../components/knowledge/rename-modal';
import { ErrorLine } from '../../components/ui/error-line';
import { Modal } from '../../components/ui/modal';
import { WorkingButton } from '../../components/ui/working-button';
import { IfWrites } from '../../components/write-access';
import {
  EVENTS_TITLE,
  EVENT_SUBLINE_FALLBACK,
  EVENT_TAB_ANALYSIS,
  EVENT_TAB_ATTENDEES,
  EVENT_TAB_LABELS,
  EVENT_DELETE_ACTION,
  EVENT_DELETE_KEEP,
  EVENT_DELETE_WORKING,
  EVENT_UNTRACK_ACTION,
  EVENT_UNTRACK_CONFIRM,
  EVENT_UNTRACK_KEEP,
  EVENT_UNTRACK_WORKING,
  KNOWLEDGE_RENAME_ACTION,
  eventDates,
  eventDeleteLine,
  eventResearchedOn,
  eventUntrackLine,
} from '../../lib/copy';
import { formatDay } from '../../lib/format';
import { Loading } from '../../components/ui/loading';
import { EmptyLine } from '../../components/ui/empty-line';
import { Button, DangerLink } from '../../components/ui/button';
import './event.css';
import { TopBar } from '../../components/ui/top-bar';
import { EventResearchAction, useEventResearch } from './research';

export interface EventRecordContext {
  entityId: string;
  record: EventRecord;
  /** Where the event's research stands, read once by the shell. */
  research: ReturnType<typeof useEventResearch>;
}

export function useEventRecord(): EventRecordContext {
  return useOutletContext<EventRecordContext>();
}

/**
 * The event's tabs, the same in every vertical: the Analysis tab shows even
 * where the vertical's menu is empty, and says so
 * (docs/notes/2026-09-24-event-analysis-flow-spec.md, ruling 4).
 */
export function eventTabs(entityId: string): { to: string; label: string; end?: boolean }[] {
  return [
    { to: `/events/${entityId}`, label: EVENT_TAB_LABELS.overview, end: true },
    { to: `/events/${entityId}/attendees`, label: EVENT_TAB_ATTENDEES },
    { to: `/events/${entityId}/analysis`, label: EVENT_TAB_ANALYSIS },
  ];
}

/**
 * **The event page** (docs/notes/2026-09-23-event-graph-design.md §3a; the
 * blend spec's record header): the top bar with Rename and research, the name
 * and its key facts with no monogram (events have none), the tabs, and the
 * record's own controls in the foot, Delete event last.
 */
export function EventRecordPage() {
  const { eventEntityId: raw = '' } = useParams();
  // Folded for the reason the other record pages fold theirs: one cache entry per event.
  const entityId = raw.toLowerCase();
  const record = useEvent(entityId);
  const untrack = useUntrackEvent(entityId);
  const navigate = useNavigate();
  const [renaming, setRenaming] = useState(false);
  const [untracking, setUntracking] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [startedRunId, setStartedRunId] = useState<string | null>(null);
  const remove = useDeleteEvent(entityId);
  const research = useEventResearch(record.data?.runs ?? [], startedRunId);

  if (record.isPending) {
    return (
      <div className="column record-column">
        <EmptyLine>
          <Loading />
        </EmptyLine>
      </div>
    );
  }
  if (record.isError) {
    return (
      <div className="column record-column">
        <TopBar parent={{ to: '/events', label: EVENTS_TITLE }} />
        <EmptyLine>{loadErrorLine(record.error)}</EmptyLine>
      </div>
    );
  }

  const subject = record.data.subject;
  const tabs = eventTabs(entityId);
  const dates = eventDates(subject.starts_on, subject.ends_on);
  const facts = [subject.place, subject.account?.label ?? null].filter((part): part is string =>
    Boolean(part),
  );

  return (
    <div className="column record-column">
      <TopBar
        parent={{ to: '/events', label: EVENTS_TITLE }}
        here={subject.display_name}
        actions={
          <IfWrites>
            <button
              type="button"
              className="quiet-action event-rename"
              onClick={() => setRenaming(true)}
            >
              {KNOWLEDGE_RENAME_ACTION}
            </button>
            {research.active ? null : (
              <EventResearchAction
                entityId={entityId}
                again={research.everCompleted}
                onStarted={setStartedRunId}
              />
            )}
          </IfWrites>
        }
      />

      <div className="event-head">
        <h1 className="event-name">{subject.display_name}</h1>
        <p className="event-facts">
          {dates === null && facts.length === 0 ? (
            EVENT_SUBLINE_FALLBACK
          ) : (
            <>
              {dates ? <b className="num">{dates}</b> : null}
              {facts.map((fact, at) => (
                <span key={fact}>
                  {dates || at > 0 ? <span className="sep">·</span> : null}
                  {fact}
                </span>
              ))}
            </>
          )}
        </p>
      </div>

      {tabs.length > 1 ? (
        <div className="tabs">
          {tabs.map((tab) => (
            <TabLink key={tab.to} end={tab.end} to={tab.to}>
              {tab.label}
            </TabLink>
          ))}
        </div>
      ) : null}

      <div className="tab-panel">
        <Outlet
          context={{ entityId, record: record.data, research } satisfies EventRecordContext}
        />
      </div>

      {/* The record's foot: when it was researched, then the controls that end
          the record. Stop tracking keeps the event in the knowledge base;
          Delete event is the destructive one, last and set apart. */}
      <div className="event-foot">
        {research.lastCompleteAt ? (
          <span className="event-foot-when">
            {eventResearchedOn(formatDay(research.lastCompleteAt))}
          </span>
        ) : null}
        <IfWrites>
          <button type="button" className="linkish flush" onClick={() => setUntracking(true)}>
            {EVENT_UNTRACK_ACTION}
          </button>
          <DangerLink onClick={() => setDeleting(true)}>{EVENT_DELETE_ACTION}</DangerLink>
        </IfWrites>
      </div>

      {deleting ? (
        <Modal label={EVENT_DELETE_ACTION} narrow onClose={() => setDeleting(false)}>
          <div className="modal-head">{EVENT_DELETE_ACTION}</div>
          <div className="modal-line">{eventDeleteLine(subject.display_name)}</div>
          {remove.isError ? <ErrorLine error={remove.error} /> : null}
          <div className="modal-actions">
            <WorkingButton
              label={EVENT_DELETE_ACTION}
              workingLabel={EVENT_DELETE_WORKING}
              variant="danger"
              working={remove.isPending}
              onClick={() => remove.mutate(undefined, { onSuccess: () => navigate('/events') })}
            />
            <Button variant="act" onClick={() => setDeleting(false)}>
              {EVENT_DELETE_KEEP}
            </Button>
          </div>
        </Modal>
      ) : null}

      {renaming ? (
        <RenameRecordModal
          here={{ entity_id: entityId, display_name: subject.display_name }}
          onClose={() => setRenaming(false)}
        />
      ) : null}

      {untracking ? (
        <Modal label={EVENT_UNTRACK_ACTION} narrow onClose={() => setUntracking(false)}>
          <div className="modal-head">{EVENT_UNTRACK_ACTION}</div>
          <div className="modal-line">{eventUntrackLine(subject.display_name)}</div>
          {untrack.isError ? <ErrorLine error={untrack.error} /> : null}
          <div className="modal-actions">
            <WorkingButton
              label={EVENT_UNTRACK_CONFIRM}
              workingLabel={EVENT_UNTRACK_WORKING}
              variant="go"
              working={untrack.isPending}
              onClick={() =>
                untrack.mutate(undefined, {
                  onSuccess: () => {
                    setUntracking(false);
                    navigate('/events');
                  },
                })
              }
            />
            <Button variant="act" onClick={() => setUntracking(false)}>
              {EVENT_UNTRACK_KEEP}
            </Button>
          </div>
        </Modal>
      ) : null}
    </div>
  );
}
